import { useState } from "react";
import axios from "axios";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Briefcase, LifeBuoy, Users, HelpCircle, Link2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE = {
  sales: { label: "Sales", icon: Briefcase, cls: "bg-[#E7F0FB] text-[#1F4E79]" },
  support: { label: "Support", icon: LifeBuoy, cls: "bg-[#E3F3E9] text-[#0E6B3F]" },
  internal: { label: "Internal", icon: Users, cls: "bg-[#F0EEE9] text-[#59554D]" },
  other: { label: "Other", icon: HelpCircle, cls: "bg-[#F0EEE9] text-[#59554D]" },
  unknown: { label: "Type unclear", icon: HelpCircle, cls: "bg-[#FFF8D6] text-[#8A6D00]" },
};

const mmss = (s) => `${Math.floor((s || 0) / 60)}m ${Math.round((s || 0) % 60)}s`;

const Section = ({ title, children, testid }) => (
  <div className="mt-2" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A]">{title}</p>
    <div className="mt-0.5 space-y-0.5">{children}</div>
  </div>
);

const Item = ({ children }) => (
  <p className="text-[12.5px] text-[#1A1A1A] leading-[1.5]">• {children}</p>
);

/* What the call was, counted where it can be counted.

   Talk share comes from the speaker-separated transcript, not from the model's
   impression of it, so a rep told they talked 78% of the call can check that
   against the recording. */
export const CallInsights = ({ recording, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const review = recording.call_review;
  const match = recording.client_match;
  if (!review && !match) return null;

  const cls = review?.classification || {};
  const t = TYPE[cls.call_type] || TYPE.unknown;
  const Icon = t.icon;
  const stats = review?.talk_stats || {};
  const sales = review?.sales || {};
  const support = review?.support || {};

  const link = async (clientId) => {
    setLinking(true);
    try {
      await axios.post(`${API}/call-import/${recording.id}/client`, { client_id: clientId });
      toast.success("Call filed against the client");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not file that call");
    } finally { setLinking(false); }
  };

  return (
    <div className="mt-2 min-w-0" data-testid={`call-insights-${recording.id}`}>
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        <Badge className={t.cls} data-testid={`call-type-${recording.id}`}>
          <Icon className="w-3 h-3 mr-1" /> {t.label}
        </Badge>
        {recording.direction && (
          <Badge className="bg-[#F0EEE9] text-[#59554D]" data-testid={`call-direction-${recording.id}`}>
            {recording.direction}
          </Badge>
        )}
        {recording.client_name ? (
          <Badge className="bg-[#FFF8D6] text-[#8A6D00]" data-testid={`call-client-${recording.id}`}>
            <Link2 className="w-3 h-3 mr-1" /> {recording.client_name}
          </Badge>
        ) : (match?.candidates || []).length > 0 ? (
          <span className="flex items-center gap-1.5 flex-wrap min-w-0 max-w-full" data-testid={`call-client-suggest-${recording.id}`}>
            <span className="text-[11.5px] text-[#8A857A]">Which client?</span>
            {match.candidates.slice(0, 3).map((c) => (
              <Button key={c.client_id} variant="outline" size="sm" disabled={linking}
                      className="h-6 text-[11.5px] border-[#DAD5CA] max-w-[180px] truncate block"
                      onClick={() => link(c.client_id)}
                      data-testid={`call-client-pick-${recording.id}-${c.client_id}`}>
                {c.company_name}
              </Button>
            ))}
          </span>
        ) : null}
        {stats.speakers?.length > 0 && (
          <span className="text-[11.5px] text-[#59554D]" data-testid={`call-talk-${recording.id}`}>
            {stats.speakers.map((s) => `${s.speaker} ${s.share}%`).join(" · ")}
          </span>
        )}
        <Button variant="ghost" size="sm" className="h-6 text-[11.5px] text-[#59554D] ml-auto"
                onClick={() => setOpen((v) => !v)} data-testid={`call-insights-toggle-${recording.id}`}>
          {open ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
          {open ? "Hide detail" : "What was said"}
        </Button>
      </div>

      {open && (
        <div className="mt-2 rounded-lg border border-[#DAD5CA] bg-[#FBFAF7] p-3" data-testid={`call-insights-body-${recording.id}`}>
          {cls.reason && <p className="text-[12.5px] text-[#59554D] italic">{cls.reason}</p>}
          {match?.evidence && !recording.client_name && (
            <p className="text-[12px] text-[#8A857A] mt-1">Matched on {match.evidence} — not certain enough to file on its own.</p>
          )}

          {(sales.commitments || []).length > 0 && (
            <Section title="Commitments" testid={`call-commitments-${recording.id}`}>
              {sales.commitments.map((c, i) => (
                <Item key={i}>{c.who === "rep" ? "We" : "They"} — {c.what}{c.due ? ` (${c.due})` : ""}</Item>
              ))}
            </Section>
          )}
          {(sales.next_steps || []).length > 0 && (
            <Section title="Next steps" testid={`call-next-steps-${recording.id}`}>
              {sales.next_steps.map((s, i) => <Item key={i}>{s}</Item>)}
            </Section>
          )}
          {(sales.objections || []).length > 0 && (
            <Section title="Objections" testid={`call-objections-${recording.id}`}>
              {sales.objections.map((o, i) => (
                <Item key={i}>{o.objection}{o.response ? ` → ${o.response}` : " → not answered on the call"}</Item>
              ))}
            </Section>
          )}
          {(sales.competitors || []).length > 0 && (
            <Section title="Competitors named" testid={`call-competitors-${recording.id}`}>
              <Item>{sales.competitors.join(", ")}</Item>
            </Section>
          )}
          {(sales.pricing_discussed || []).length > 0 && (
            <Section title="Pricing discussed" testid={`call-pricing-${recording.id}`}>
              {sales.pricing_discussed.map((p, i) => (
                <Item key={i}>{p.item} — {p.amount}{p.seats ? `, ${p.seats}` : ""} <span className="text-[#8A857A]">(as said on the call)</span></Item>
              ))}
            </Section>
          )}
          {sales.risk && sales.risk !== "none" && (
            <Section title={`Deal risk: ${sales.risk}`} testid={`call-risk-${recording.id}`}>
              <Item>{sales.risk_reason || "No reason given"}</Item>
            </Section>
          )}
          {(sales.rep_feedback?.strengths || []).length > 0 && (
            <Section title="Rep — what worked" testid={`call-rep-good-${recording.id}`}>
              {sales.rep_feedback.strengths.map((s, i) => <Item key={i}>{s}</Item>)}
            </Section>
          )}
          {(sales.rep_feedback?.improvements || []).length > 0 && (
            <Section title="Rep — what to change" testid={`call-rep-improve-${recording.id}`}>
              {sales.rep_feedback.improvements.map((s, i) => <Item key={i}>{s}</Item>)}
            </Section>
          )}

          {(support.product_areas || []).length > 0 && (
            <Section title="Product areas" testid={`call-areas-${recording.id}`}>
              <Item>{support.product_areas.join(", ")}</Item>
            </Section>
          )}
          {support.resolved && (
            <Section title="Resolved" testid={`call-resolved-${recording.id}`}>
              <Item>{support.resolved}</Item>
            </Section>
          )}
          {(support.follow_ups || []).length > 0 && (
            <Section title="Follow-ups" testid={`call-followups-${recording.id}`}>
              {support.follow_ups.map((s, i) => <Item key={i}>{s}</Item>)}
            </Section>
          )}
          {(support.churn_signals || []).length > 0 && (
            <Section title="Worth watching" testid={`call-churn-${recording.id}`}>
              {support.churn_signals.map((s, i) => <Item key={i}>{s}</Item>)}
            </Section>
          )}
          {(support.upsell_signals || []).length > 0 && (
            <Section title="Asked for more" testid={`call-upsell-${recording.id}`}>
              {support.upsell_signals.map((s, i) => <Item key={i}>{s}</Item>)}
            </Section>
          )}

          {stats.speakers?.length > 0 && (
            <Section title="Who spoke" testid={`call-talk-detail-${recording.id}`}>
              {stats.speakers.map((s) => (
                <Item key={s.speaker}>
                  {s.speaker}: {s.share}% of {mmss(stats.total_seconds)} · longest turn {mmss(s.longest_turn_seconds)} · {s.questions_asked} question(s)
                </Item>
              ))}
            </Section>
          )}
          {recording.drive_link && (
            <a href={recording.drive_link} target="_blank" rel="noreferrer"
               className="inline-block mt-2 text-[12px] text-[#1F4E79] underline"
               data-testid={`call-drive-link-${recording.id}`}>
              Open the original in Drive
            </a>
          )}
        </div>
      )}
    </div>
  );
};
